import * as React from 'react'

import { Button, CloseIcon, IconButton, ZapIcon } from '@buffer-mono/popcorn'

import { useCta } from '~publish/hooks/useCta'
import { SEGMENT_NAMES } from '~publish/legacy/constants'

import UpgradePath from '../../../upgrade-paths/index'
import NotificationActionCreators from '../action-creators/NotificationActionCreators'
import { NotificationTypes } from '../AppConstants'

import styles from './css/Notification.module.css'

interface NotificationCtaProps {
  cta: {
    isUpgradePath: boolean
    label: string
    action: () => void
  }
}

const NotificationCta = ({ cta }: NotificationCtaProps): JSX.Element => {
  const { isUpgradePath, label, action } = cta
  const upgradePathName = 'queueLimit-upgrade'
  const ctaHook = useCta(SEGMENT_NAMES.COMPOSER_QUEUE_LIMIT as CTA, {
    upgradePathName,
  })

  return (
    <div className={styles.ctaContainer}>
      {isUpgradePath ? (
        <UpgradePath>
          {(onUpgradeClick): JSX.Element => (
            <Button
              variant="primary"
              onClick={(e: React.MouseEvent<HTMLElement>): void => {
                e.preventDefault()
                ctaHook.clicked()
                onUpgradeClick({
                  cta: SEGMENT_NAMES.COMPOSER_QUEUE_LIMIT,
                  upgradePathName,
                })
              }}
            >
              <ZapIcon />
              {label}
            </Button>
          )}
        </UpgradePath>
      ) : (
        <Button variant="primary" onClick={action}>
          {label}
        </Button>
      )}
    </div>
  )
}

interface NotificationProps {
  id: string
  type: string
  message: string
  showCloseIcon: boolean
  showSoftAndHardCloseOptions?: boolean
  onClose?: (id: string, options?: any) => void
  className?: string | null
  classNames?: {
    notification?: string | null
    closeButton?: string | null
  }
  cta?: {
    label: string
    action: () => void
    isUpgradePath: boolean
  } | null
  children?: React.ReactNode
}

const Notification = (args: NotificationProps): JSX.Element => {
  const {
    id,
    type,
    message,
    showCloseIcon,
    showSoftAndHardCloseOptions = false,
    onClose,
    className = null,
    classNames = {
      notification: null,
      closeButton: null,
    },
    cta = null,
    children = null,
  } = args

  const hardCloseCheckboxRef = React.useRef<HTMLInputElement>(null)

  const onCloseButtonClick = (): void => {
    const data = showSoftAndHardCloseOptions
      ? {
          isHardCloseCheckboxChecked: hardCloseCheckboxRef.current?.checked,
          shouldCloseVisibleNotification: true,
        }
      : {}
    NotificationActionCreators.removeNotification(id, data)
    onClose?.(id)
  }

  const onCheckboxChange = (): void => {
    const data = showSoftAndHardCloseOptions
      ? {
          isHardCloseCheckboxChecked: hardCloseCheckboxRef.current?.checked,
          shouldCloseVisibleNotification: false,
        }
      : {}

    if (hardCloseCheckboxRef.current?.checked) {
      NotificationActionCreators.removeNotification(id, data)
      onClose?.(id)
    } else if (hardCloseCheckboxRef.current?.checked === false) {
      // if a user unchecks the notification, remove the cookie that was added
      NotificationActionCreators.removeNotificationCookie(id)
    }
  }

  const htmlMessage = { __html: message }

  const notificationClassNamesMap = {
    [NotificationTypes.ERROR]: styles.errorNotification,
    [NotificationTypes.SUCCESS]: styles.successNotification,
    [NotificationTypes.INFO]: styles.infoNotification,
  }

  let closeIconClassName

  if (showCloseIcon) {
    closeIconClassName = showSoftAndHardCloseOptions
      ? styles.notificationWithCloseButtonAndCheckbox
      : styles.notificationWithCloseButton
  } else {
    closeIconClassName = styles.notification
  }

  let queueLimitClassName
  if (cta?.label === 'Upgrade' && cta?.isUpgradePath) {
    queueLimitClassName = styles.queueLimitReached
  }

  const notificationClassName = [
    closeIconClassName,
    notificationClassNamesMap[type as keyof typeof notificationClassNamesMap],
    classNames.notification || className,
    queueLimitClassName,
  ].join(' ')

  const closeButtonClassName = [
    styles.closeButton,
    classNames.closeButton,
  ].join(' ')

  return (
    <div className={notificationClassName}>
      <div
        className={styles.notificationContent}
        dangerouslySetInnerHTML={htmlMessage}
      />
      {cta && <NotificationCta cta={cta} />}

      {showCloseIcon && (
        <IconButton
          className={closeButtonClassName}
          label={'Dismiss notification'}
          size="small"
          variant="tertiary"
          onClick={onCloseButtonClick}
        >
          <CloseIcon />
        </IconButton>
      )}

      {children}

      {showSoftAndHardCloseOptions && (
        <div className={styles.hardCloseCheckboxContainer}>
          {}
          <label className={styles.hardCloseCheckboxLabel}>
            <input
              type="checkbox"
              className={styles.hardCloseCheckbox}
              onChange={onCheckboxChange}
              ref={hardCloseCheckboxRef}
            />
            Don&apos;t show this message again
          </label>
        </div>
      )}
    </div>
  )
}

export default Notification
